import { Reservation } from "@/types/Reservation";
import { format, set } from "date-fns";

export const SECOND = 1000;

export const MINUTE = SECOND * 60;

export const HOUR = MINUTE * 60;

export const DAY = HOUR * 24;

export const getTimeDiff = (date1: Date, date2: Date): number => {
  return date1.getTime() - date2.getTime();
};

export const getNumberOfSeconds = (time: number): number => {
  return Math.floor(time / SECOND);
};

export const getMinutesWithoutHours = (time: number): number => {
  return Math.floor((time % HOUR) / MINUTE);
};

export const getNumberOfMinutes = (time: number): number => {
  return Math.floor(time / MINUTE);
};

export const getNumberOfHours = (time: number): number => {
  return Math.floor(time / HOUR);
};

export const getNumberOfDays = (time: number): number => {
  const retv = Math.floor(time / DAY);
  return retv;
};

export const getMsTillMidnight = (timestamp: number): number => {
  return DAY - (timestamp - set(timestamp, { hours: 0, minutes: 0, seconds: 0 }).getTime());
};

export const getDayDifference = (start: Date, end: Date): number => {
  const date1 = new Date(format(start, "P"));
  const date2 = new Date(format(end, "P"));

  const diffInTime = getTimeDiff(date2, date1);

  const diffInDays = Math.floor(diffInTime / DAY);

  return diffInDays;
};

/**
 * How long after local midnight the day-rollover backstop may run, and the jitter window added on
 * top of it.
 *
 * The delay clears `App.vue`'s reload window (midnight + up to 60s), so on a normal night the
 * reload refetches first and this backstop never fires. The jitter exists because a fixed instant
 * puts every tablet in a timezone on the same second — that is what produced the midnight request
 * spike behind DT-1103.
 */
export const DAILY_REFRESH_BACKSTOP_DELAY = 90 * SECOND;
export const DAILY_REFRESH_BACKSTOP_JITTER = 5 * MINUTE;

/**
 * Local calendar date as `yyyy-MM-dd`, used to detect a day rollover.
 *
 * Deliberately not a fixed 24h elapsed-time comparison: local days are 23h or 25h across DST
 * transitions. On a 25h fall-back day a `now - anchor > DAY` test turns true an hour *before* local
 * midnight, while re-anchoring still resolves to the same day — so the condition stays true and
 * re-fires on every clock tick until midnight.
 */
export const getLocalDateKey = (date: Date): string => format(date, "yyyy-MM-dd");

/** Milliseconds elapsed since local midnight. DST-safe: derived from the local wall clock. */
export const msSinceLocalMidnight = (date: Date): number =>
  date.getTime() - set(date, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }).getTime();

/**
 * Formats a Date as a time-of-day string in either 12-hour or 24-hour notation.
 *
 * 12h → "1:30 PM"  (date-fns token "h:mm a")
 * 24h → "13:30"    (date-fns token "HH:mm")
 */
export const formatTime = (date: Date, timeFormat: 12 | 24): string => {
  return format(date, timeFormat === 24 ? "HH:mm" : "h:mm a");
};

export const remainingTimeOfReservationInfo = (
  firstFutureReservation: Reservation,
  now: Date
): { diffHours: number | null; diffMinutes: number | null } => {
  if (!firstFutureReservation) {
    return { diffHours: null, diffMinutes: null };
  }
  const timeDiff = getTimeDiff(firstFutureReservation.startTime, now) + MINUTE;
  const diffHours = getNumberOfHours(timeDiff);
  const diffMinutes = getMinutesWithoutHours(timeDiff);

  return { diffHours, diffMinutes };
};

// A host with no resolvable zone (a container without tzdata, or an unparseable `TZ`) makes Chrome
// return either the literal "Etc/Unknown" or `undefined`. Neither is an IANA identifier, so the API
// rejects both — validate before sending one.
export const isValidTimeZone = (timeZone?: string | null): timeZone is string => {
  if (!timeZone) return false;

  try {
    Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
};

// Returns undefined rather than a substitute when the device zone is unusable, so callers can omit
// `time_zone` entirely. The schedule endpoint defaults an absent parameter to the organization's own
// zone (`LocalSchedules#now_in_time_zone`), which is the right answer for a wall-mounted device —
// sending UTC instead would silently shift the day boundary and show the wrong day's bookings.
export const deviceTimeZone = (): string | undefined => {
  const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return isValidTimeZone(resolved) ? resolved : undefined;
};
